import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { adminRoutes } from '../routes';

function SidebarUser({ onOpen }) {
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('admin');
      if (raw) setAdmin(JSON.parse(raw));
    } catch (e) {
      // ignore
    }
  }, []);

  if (!admin) return null;

  const firstLetter = admin.name ? admin.name.charAt(0).toUpperCase() : 'A';

  return (
    <div className="sidebar-user" onClick={() => onOpen(admin)}>
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'var(--accent)',
        color: '#0b1735',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.9rem',
        fontWeight: '700',
        marginRight: '12px',
        flexShrink: 0
      }}>
        {firstLetter}
      </div>
      <div className="sidebar-user-name">{admin.name}</div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileAdmin, setProfileAdmin] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  // Get current admin data
  useEffect(() => {
    try {
      const raw = localStorage.getItem('admin');
      if (raw) {
        const admin = JSON.parse(raw);
        setCurrentAdmin(admin);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Hide sidebar for login pages, public games route, or role 3 users
  const hideSidebar = location.pathname === '/login' || 
                     location.pathname.startsWith('/login') || 
                     location.pathname === '/games' ||
                     (currentAdmin && (currentAdmin.role === 3 || currentAdmin.role === '3'));

  const openProfile = (admin) => {
    setProfileAdmin(admin);
    setProfileOpen(true);
  };

  const closeProfile = () => setProfileOpen(false);

  const handleLogout = () => {
    try {
      localStorage.removeItem('admin');
    } catch (e) {}
    setProfileOpen(false);
    navigate('/login', { replace: true });
  };

  return (
    <div className="admin-app">
      {!hideSidebar && (
        <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              {!sidebarCollapsed && (
                <img 
                  src="/Gaming Science logo.webp" 
                  alt="Gaming Science" 
                  style={{
                    height: '42px',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
            <button 
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {sidebarCollapsed ? (
                  <path d="M9 18l6-6-6-6"/>
                ) : (
                  <path d="M15 18l-6-6 6-6"/>
                )}
              </svg>
            </button>
          </div>
          
          {!sidebarCollapsed && (
            <>
              <nav className="sidebar-nav">
                {adminRoutes.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `sidebar-link${isActive ? ' active' : ''}`
                    }
                  >
                    <span style={{ fontSize: '1.1rem', marginRight: '8px' }}>{link.icon}</span>
                    {link.label}
                  </NavLink>
                ))}
              </nav>

              <div style={{ marginTop: 'auto' }}>
                <SidebarUser onOpen={openProfile} />
                <div className="sidebar-footer">© {new Date().getFullYear()} Gaming Science</div>
              </div>
            </>
          )}
        </aside>
      )}

      <main className={`admin-content ${hideSidebar ? 'full-width' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${(location.pathname === '/admin/games' || location.pathname === '/games') ? 'games-page' : ''}`}>{children}</main>

      {profileOpen && profileAdmin && (
        <div className="profile-modal-backdrop" onClick={closeProfile}>
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: 'var(--text-primary)', fontSize: '1.3rem', fontWeight: '600' }}>
                User Profile
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.85rem', 
                    fontWeight: '500',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Name
                  </label>
                  <div style={{ 
                    color: 'var(--text-primary)', 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: 'var(--border-radius-sm)',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}>
                    {profileAdmin.name || 'N/A'}
                  </div>
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.85rem', 
                    fontWeight: '500',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Email ID
                  </label>
                  <div style={{ 
                    color: 'var(--text-primary)', 
                    fontSize: '1rem', 
                    fontWeight: '500',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: 'var(--border-radius-sm)',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}>
                    {profileAdmin.email || 'N/A'}
                  </div>
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    color: 'var(--text-secondary)', 
                    fontSize: '0.85rem', 
                    fontWeight: '500',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Role
                  </label>
                  <div style={{ 
                    color: 'var(--text-primary)', 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: 'var(--border-radius-sm)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: profileAdmin.role === 1 || profileAdmin.role === '1' ? 'var(--accent)' : 
                                 profileAdmin.role === 2 || profileAdmin.role === '2' ? '#ffc107' : '#9b59ff'
                    }}></span>
                    {profileAdmin.role === 1 || profileAdmin.role === '1' ? 'Admin' : 
                     profileAdmin.role === 2 || profileAdmin.role === '2' ? 'Support' : 
                     profileAdmin.role === 3 || profileAdmin.role === '3' ? 'User' : 
                     'Unknown Role'}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <button 
                className="button-secondary" 
                onClick={() => { closeProfile(); }}
                style={{ padding: '10px 20px', fontSize: '0.9rem', fontWeight: '600' }}
              >
                Close
              </button>
              <button 
                className="button-danger" 
                onClick={handleLogout}
                style={{ padding: '10px 20px', fontSize: '0.9rem', fontWeight: '600' }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
